import { Flex, Form, Radio, Tag, Typography } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FEEDBACK_RATING_FIELDS } from '@/components/form/feedbackRatingFields';
import FormItem from '@/components/form/FormItem';
import { SPACE_XL } from '@/theme/buildAntdTheme';
import { Label } from '@/UI/typography';

import type { FeedbackFormValues } from '@/components/form/FeedbackForm';

type FeedbackResultData = Partial<FeedbackFormValues>;

interface FeedbackProps {
  data: FeedbackResultData;
}

const FeedbackResult = ({ data }: FeedbackProps) => {
  const [form] = Form.useForm();
  const hydratedDataRef = useRef<string | null>(null);

  useEffect(() => {
    const identityKey = JSON.stringify(data);
    if (hydratedDataRef.current === identityKey) return;
    hydratedDataRef.current = identityKey;
    form.setFieldsValue(data);
  }, [data, form]);

  const [t] = useTranslation();

  const feedbacks: { name: string; value: string }[] = [
    { name: t('NEGATIVE'), value: 'negative' },
    { name: t('NEUTRAL'), value: 'neutral' },
    { name: t('POSITIVE'), value: 'positive' },
  ];

  return (
    <Form form={form} scrollToFirstError={true}>
      <Flex vertical gap={SPACE_XL}>
        <Typography.Title level={5}>
          {t('RESPONDENT_DECISION')}
        </Typography.Title>

        {FEEDBACK_RATING_FIELDS.map(
          ({ ratingField, commentField, labelKey }) => {
            const comment = data[commentField];
            return (
              <Flex key={ratingField} vertical>
                <Label>{t(labelKey)}</Label>

                <FormItem
                  name={ratingField}
                  initialValue={data[ratingField]}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    disabled
                    optionType="button"
                    buttonStyle="solid"
                    block
                  >
                    {feedbacks.map((item) => (
                      <Radio.Button key={item.value} value={item.value}>
                        {item.name}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </FormItem>
                {comment && (
                  <Typography.Text type="secondary">{comment}</Typography.Text>
                )}
              </Flex>
            );
          },
        )}

        <Flex vertical>
          <Label>{t('GENERAL_COMMENTS')}</Label>
          {data.comments && (
            <Typography.Text type="secondary">{data.comments}</Typography.Text>
          )}
        </Flex>

        <Flex vertical align="flex-start">
          <Label>{t('OVERALL_DECISION')}</Label>
          <Tag color={data.decision === 'positive' ? 'success' : 'error'}>
            {t(`${data.decision?.toUpperCase() ?? ''}_DECISION`)}
          </Tag>
        </Flex>
      </Flex>
    </Form>
  );
};

export default FeedbackResult;
